import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  Sheet,
  Button,
  Select,
  Option,
  FormControl,
  FormLabel,
  Grid,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
} from '@mui/joy';
import {
  ArrowsClockwise as Refresh,
  DownloadSimple as Download,
  Info as InfoOutlined,
  Bank as AccountBalance,
  TrendUp as TrendingUp,
  TrendDown as TrendingDown,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../../lib/apiAccounting';
import type { AccountActivityReport, ChartOfAccount } from '../../../types/accounting';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const AccountActivityReportPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AccountActivityReport | null>(null);
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );

  // Fetch accounts on mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await apiAccountingClient.getChartOfAccounts();
        setAccounts(response.results || []);
        // Auto-select first account if available
        if (response.results && response.results.length > 0) {
          setSelectedAccountId(response.results[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch accounts:', err);
        setError('Failed to load accounts');
      }
    };
    fetchAccounts();
  }, []);

  const fetchReport = async () => {
    if (!selectedAccountId) {
      setError('Please select an account');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await apiAccountingClient.getAccountActivityReport(
        selectedAccountId,
        startDate,
        endDate
      );
      setData(response);
    } catch (err) {
      setError('Failed to load account activity report');
      console.error('Report error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAccountId) {
      fetchReport();
    }
  }, [selectedAccountId, startDate, endDate]);

  const handleExport = async () => {
    if (!data) return;

    // Create CSV content
    const headers = [
      'Date',
      'Entry Number',
      'Description',
      'Reference',
      'Debit',
      'Credit',
      'Balance'
    ];

    const rows = data.transactions.map(tx => [
      formatDate(tx.date),
      tx.entry_number,
      tx.description,
      tx.reference || '',
      tx.debit ? formatCurrency(tx.debit) : '',
      tx.credit ? formatCurrency(tx.credit) : '',
      formatCurrency(tx.balance)
    ]);

    // Add opening balance row
    rows.unshift([
      formatDate(data.period.start_date),
      '',
      'Opening Balance',
      '',
      '',
      '',
      formatCurrency(data.summary.opening_balance)
    ]);

    // Create CSV
    const csvContent = [
      `Account Activity Report - ${data.account.name}`,
      `Period: ${formatDate(data.period.start_date)} to ${formatDate(data.period.end_date)}`,
      '',
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `account-activity-${data.account.account_number}-${startDate}-to-${endDate}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3 }}>
        <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Account Activity Report</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            onClick={fetchReport}
            disabled={loading || !selectedAccountId}
            variant="outlined"
          >
            <Refresh />
          </IconButton>
          <Button
            startDecorator={<Download />}
            onClick={handleExport}
            disabled={!data}
            variant="outlined"
            color="primary"
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <CollapsibleFilters><Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid xs={12} md={4}>
              <FormControl>
                <FormLabel>Account</FormLabel>
                <Select
                  value={selectedAccountId}
                  onChange={(_, value) => setSelectedAccountId(value as number)}
                  placeholder="Select an account"
                >
                  {accounts.map(account => (
                    <Option key={account.id} value={account.id}>
                      {account.account_number} - {account.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl>
                <FormLabel>Start Date</FormLabel>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px'
                  }}
                />
              </FormControl>
            </Grid>
            <Grid xs={12} md={4}>
              <FormControl>
                <FormLabel>End Date</FormLabel>
                <input
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  style={{
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px'
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card></CollapsibleFilters>

      {error && (
        <Alert color="danger" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {data && !loading && (
        <>
          {/* Account Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccountBalance color="var(--joy-palette-primary-500)" style={{ marginRight: 16 }} />
                <Box>
                  <Typography level="title-lg">
                    {data.account.name}
                  </Typography>
                  <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                    Account #{data.account.account_number} • {data.account.account_type} •
                    Normal Balance: {data.account.normal_balance}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography level="body-sm" sx={{ mb: 1 }}>
                    Opening Balance
                  </Typography>
                  <Typography level="h3">
                    {formatCurrency(data.summary.opening_balance)}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    As of {formatDate(data.period.start_date)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingUp size={16} color="var(--joy-palette-success-500)" style={{ marginRight: 8 }} />
                    <Typography level="body-sm">Total Debits</Typography>
                  </Box>
                  <Typography level="h3">
                    {formatCurrency(data.summary.total_debits)}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    {data.summary.transaction_count} transactions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <TrendingDown size={16} color="var(--joy-palette-warning-500)" style={{ marginRight: 8 }} />
                    <Typography level="body-sm">Total Credits</Typography>
                  </Box>
                  <Typography level="h3">
                    {formatCurrency(data.summary.total_credits)}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    Net Change: {formatCurrency(data.summary.net_change)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography level="body-sm" sx={{ mb: 1 }}>
                    Ending Balance
                  </Typography>
                  <Typography level="h3" sx={{
                    color: data.summary.ending_balance < 0 ? 'danger.main' : 'inherit'
                  }}>
                    {formatCurrency(data.summary.ending_balance)}
                  </Typography>
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    As of {formatDate(data.period.end_date)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Transactions Table */}
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Sheet
                variant="outlined"
                sx={{
                  overflow: 'auto',
                  borderRadius: 'sm',
                  '& > table': { borderCollapse: 'collapse', width: '100%' }
                }}
              >
                <Table stickyHeader sx={{ minWidth: 820 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 100 }}>Date</th>
                      <th style={{ width: 120 }}>Entry #</th>
                      <th>Description</th>
                      <th style={{ width: 100 }}>Reference</th>
                      <th style={{ width: 100, textAlign: 'right' }}>Debit</th>
                      <th style={{ width: 100, textAlign: 'right' }}>Credit</th>
                      <th style={{ width: 120, textAlign: 'right' }}>Balance</th>
                      <th style={{ width: 80 }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Opening Balance Row */}
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <td>{formatDate(data.period.start_date)}</td>
                      <td>-</td>
                      <td><strong>Opening Balance</strong></td>
                      <td>-</td>
                      <td style={{ textAlign: 'right' }}>-</td>
                      <td style={{ textAlign: 'right' }}>-</td>
                      <td style={{ textAlign: 'right' }}>
                        <strong>{formatCurrency(data.summary.opening_balance)}</strong>
                      </td>
                      <td>-</td>
                    </tr>

                    {/* Transaction Rows */}
                    {data.transactions.map((tx, index) => (
                      <tr key={index}>
                        <td>{formatDate(tx.date)}</td>
                        <td>{tx.entry_number}</td>
                        <td>{tx.description}</td>
                        <td>{tx.reference || '-'}</td>
                        <td style={{ textAlign: 'right' }}>
                          {tx.debit > 0 ? formatCurrency(tx.debit) : '-'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {tx.credit > 0 ? formatCurrency(tx.credit) : '-'}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <strong>{formatCurrency(tx.balance)}</strong>
                        </td>
                        <td>
                          <Chip
                            size="sm"
                            variant="soft"
                            color={tx.entry_status === 'POSTED' ? 'success' : 'neutral'}
                          >
                            {tx.entry_status}
                          </Chip>
                        </td>
                      </tr>
                    ))}

                    {/* Summary Row */}
                    <tr style={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                      <td colSpan={4}>Total</td>
                      <td style={{ textAlign: 'right' }}>
                        {formatCurrency(data.summary.total_debits)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {formatCurrency(data.summary.total_credits)}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {formatCurrency(data.summary.ending_balance)}
                      </td>
                      <td>-</td>
                    </tr>
                  </tbody>
                </Table>
              </Sheet>
            </CardContent>
          </Card>
        </>
      )}

      {!loading && !data && selectedAccountId && (
        <Alert
          startDecorator={<InfoOutlined />}
          color="neutral"
        >
          Select an account and date range to view transaction activity
        </Alert>
      )}
    </Box>
  );
};
